// Section.tsx
import { Box, Typography, Paper } from '@mui/material';
import React from 'react';

interface SectionProps {
  children: React.ReactNode;
  id?: string;
}

export const SectionContainer: React.FC<SectionProps> = ({ children, id }) => (
  <Paper
    id={id}
    component="section"
    elevation={0}
    sx={{
      mb: 4,
      p: { xs: 3, md: 4 },
      backgroundColor: '#ffffff',
      borderRadius: 3,
      border: '1px solid rgba(0,0,0,0.08)',
      boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
      transition: 'all 0.3s ease',
      '&:hover': {
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
      },
      '@media print': {
        backgroundColor: '#ffffff',
        border: '1px solid #e0e0e0',
        borderRadius: 0,
        boxShadow: 'none',
        p: 3,
        mb: 3,
        pageBreakInside: 'avoid',
        '&:hover': {
          boxShadow: 'none'
        }
      }
    }}
  >
    {children}
  </Paper>
);

export const SectionTitle: React.FC<SectionProps> = ({ children }) => (
  <Box sx={{ mb: 3 }}>
    <Typography
      variant="h5"
      component="h2"
      sx={{
        color: '#1565c0',
        fontWeight: 700,
        fontSize: { xs: '1.25rem', md: '1.5rem' },
        letterSpacing: '-0.25px',
        lineHeight: 1.3,
        mb: 1,
        '@media print': {
          color: '#1a237e',
          fontSize: '1.25rem'
        }
      }}
    >
      {children}
    </Typography>
    <Box
      sx={{
        width: 60,
        height: 3,
        background: 'linear-gradient(90deg, #1565c0 0%, #42a5f5 100%)',
        borderRadius: 2,
        '@media print': {
          background: '#1565c0'
        }
      }}
    />
  </Box>
);

export const SectionBody: React.FC<SectionProps> = ({ children }) => (
  <Box
    sx={{
      color: '#37474f',
      fontSize: '0.95rem',
      lineHeight: 1.7,
      '& p': {
        mb: 2,
        fontSize: '0.95rem',
        lineHeight: 1.7
      },
      '& h6': {
        color: '#1565c0',
        fontWeight: 600,
        mb: 1.5,
        mt: 2
      },
      '& ul, & ol': {
        pl: 2,
        '& li': {
          mb: 0.5,
          fontSize: '0.95rem',
          lineHeight: 1.6
        }
      },
      '@media print': {
        color: '#000000',
        fontSize: '0.875rem',
        '& p': {
          fontSize: '0.875rem',
          mb: 1.5
        },
        '& h6': {
          color: '#1a237e'
        },
        '& ul, & ol': {
          '& li': {
            fontSize: '0.875rem'
          }
        }
      }
    }}
  >
    {children}
  </Box>
);

// New component for subsections
export const SubSectionTitle: React.FC<SectionProps> = ({ children }) => (
  <Typography
    variant="h6"
    component="h3"
    sx={{
      color: '#1976d2',
      fontWeight: 600,
      fontSize: '1.125rem',
      mb: 2,
      mt: 3,
      letterSpacing: '-0.15px',
      display: 'flex',
      alignItems: 'center',
      '&::before': {
        content: '""',
        width: 4,
        height: 20,
        backgroundColor: '#1976d2',
        borderRadius: 2,
        mr: 2
      },
      '@media print': {
        color: '#1a237e',
        fontSize: '1rem',
        mt: 2,
        '&::before': {
          backgroundColor: '#1a237e'
        }
      }
    }}
  >
    {children}
  </Typography>
);

// New component for metric highlights
export const MetricHighlight: React.FC<{
  label: string;
  value: string | number;
  color?: string;
}> = ({ label, value, color = '#1976d2' }) => (
  <Box
    sx={{
      display: 'inline-flex',
      flexDirection: 'column',
      alignItems: 'center',
      p: 2,
      borderRadius: 2,
      backgroundColor: `${color}08`,
      border: `1px solid ${color}20`,
      minWidth: 120,
      textAlign: 'center',
      transition: 'all 0.3s ease',
      '&:hover': {
        backgroundColor: `${color}12`,
        transform: 'translateY(-2px)'
      },
      '@media print': {
        backgroundColor: 'transparent',
        border: `1px solid ${color}`,
        '&:hover': {
          backgroundColor: 'transparent',
          transform: 'none'
        }
      }
    }}
  >
    <Typography
      variant="body2"
      sx={{
        color: '#666',
        fontWeight: 500,
        mb: 0.5,
        fontSize: '0.8rem',
        textTransform: 'uppercase',
        letterSpacing: 0.5
      }}
    >
      {label}
    </Typography>
    <Typography
      variant="h6"
      sx={{
        color: color,
        fontWeight: 700,
        fontSize: '1.25rem',
        lineHeight: 1
      }}
    >
      {value}
    </Typography>
  </Box>
);
